import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Tractor, FlaskConical, Map, Sprout, ArrowLeft, ArrowRight, Sparkles, ShieldCheck
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface AgriCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const AGRI_AGENT_IDS = [
  'agri-grain-quality-elevator-auditor',
  'agri-cadastre-gis-auditor',
  'agri-eu-cap-subsidies-advisor',
  'agri-dar-consultant',
  'agri-soil-analyst',
  'agri-land-audit',
  'agri-land-consultant',
  'agri-yield-forecaster',
  'agri-mpz-calculator'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Tractor className={className} />;
  return <IconComponent className={className} />;
};

export default function AgriCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: AgriCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch agri pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const agriAgents = agentsConfig.filter((a: any) => AGRI_AGENT_IDS.includes(a.id));

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            {isPl ? '🌾 6 Modułów Agro & Gruntów' : isUk ? '🌾 6 Мультиагентних Агро-Експертів' : '🌾 6 Agri & Land Agents'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-400" />
            {isPl ? 'ARiMR • NPK • EGiB • NDVI Sentinel' : isUk ? 'ДАР • NPK • ДЗК • NDVI • ДСТУ 4163' : 'ARiMR & Precision Farming'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
              {isPl ? 'AGROBIZNES • GRUNTY • DOPŁATY ARiMR • ANALIZA GLEB NPK • NDVI' : isUk ? 'ЦЕНТР АГРОБІЗНЕСУ, ЗЕМЛІ ТА ТОЧНОГО ЗЕМЛЕРОБСТВА' : 'AGRIBUSINESS, LAND & PRECISION FARMING'}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide text-white">
            {isPl ? '🌾 CENTRUM AGRO, GRUNTÓW & ROLNICTWA PRECYZYJNEGO' : isUk ? '🌾 АГРО-ЦЕНТР & ЗЕМЕЛЬНИЙ КОМПЛАЄНС' : '🌾 AGRI COMMAND CENTER & LAND COMPLIANCE'}
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Wielomodułowe rozwiązania do doboru dotacji i dopłat rolniczych (ARiMR/WPR), analizy agrochemicznej gleb NPK, audytu umów dzierżawy gruntów oraz satelitarnego prognozowania plonów NDVI.'
              : isUk
              ? 'Спеціалізовані мультиагентні CrewAI класи для автоматизованого підбору дотацій та субсидій у ДАР, аналізу ґрунтових проб NPK, перевірки договорів оренди земельних паїв та супутникового прогнозування врожайності посівів.'
              : 'Specialized multi-agent suite for farming grants matching, soil NPK analysis, land lease audit, and satellite crop yield forecasting.'}
          </p>
        </div>
      </div>

      {/* Featured Banner: AgroDeepResearchHub Satellite Multi-Hazard Monitor */}
      <div className="bg-gradient-to-r from-emerald-950/80 via-slate-900 to-slate-950 border-2 border-emerald-500 rounded-2xl p-6 text-white shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500 text-slate-950 rounded font-mono">
              НОВИЙ МОДУЛЬ 2026
            </span>
            <span className="px-2 py-0.5 text-[9px] font-bold text-amber-300 bg-amber-950/80 rounded border border-amber-500/30">
              ⚡ Супутники Sentinel + Open-Meteo + Telegram
            </span>
          </div>
          <h2 className="text-xl font-extrabold uppercase text-white tracking-wide flex items-center gap-2">
            <span>🛰️ AgroDeepResearchHub: Мультиризиковий Агромонітор</span>
          </h2>
          <p className="text-xs text-slate-300 font-mono leading-relaxed">
            Автономний космічний моніторинг 5 ключових агрозагроз: посуха (NDMI), екстремальна спека (&gt;32°C), нічні заморозки (≤0°C на ґрунті), стрімке падіння вегетації (NDVI) та зливи зі шквалами. Миттєві сповіщення у боті @Agrodeepresearchhub_bot при кожному оновленні супутників!
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full lg:w-auto">
          <button
            onClick={() => onLaunchAgent('agro-deep-research-hub')}
            className="w-full sm:w-auto px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer"
          >
            <Tractor className="w-4 h-4" />
            <span>Відкрити Веб-Монітор</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href="https://t.me/Agrodeepresearchhub_bot"
            target="_blank"
            rel="noreferrer"
            className="w-full sm:w-auto px-5 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-extrabold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Telegram Бот</span>
          </a>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {agriAgents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="bg-slate-950 border border-emerald-500/30 hover:border-emerald-400 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all hover:scale-[1.01] shadow-xl group"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded">
                    {getLocalizedAgentCategory(agent, language)}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
                    ✨ {agent.model === 'gemini-3.7-flash' ? 'Gemini 3.7 Flash' : 'Gemini 3.7 Flash'}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded font-mono">
                    {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                  </span>
                  {singlePriceVal > 0 && (
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded font-mono">
                      {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-[9px] font-bold text-amber-300 bg-amber-950/60 rounded border border-amber-500/30">
                    {isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}
                  </span>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-emerald-950/80 border border-emerald-500/30 rounded-xl text-emerald-400 shrink-0 group-hover:text-emerald-300 transition-colors">
                    <AgentIcon name={agent.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-white uppercase group-hover:text-emerald-300 transition-colors leading-snug">
                      {getLocalizedAgentName(agent, language)}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1 font-mono leading-relaxed line-clamp-3">
                      {getLocalizedAgentDesc(agent, language)}
                    </p>
                  </div>
                </div>

                {/* Key Features List */}
                <div className="space-y-1.5 pt-2 border-t border-slate-800">
                  {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                    <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-300 font-mono">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-slate-800">
                <button
                  onClick={() => onLaunchAgent(agent.id)}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>{isPl ? '🚀 Otwórz Moduł Agro' : isUk ? '🚀 Відкрити Агро-Модуль' : '🚀 Launch Agri Module'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

